import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { type DocNode } from '@atlaskit/adf-schema/doc';

import { type SelectedAgentTransitionHandle } from './useSelectedAgentTransition';

type DraftNode = {
	type?: string;
	attrs?: {
		id?: string;
		localId?: string;
		userType?: string;
		appType?: string;
	};
	content?: DraftNode[];
};

type RestoredAgentMentionMetadata = {
	/** The editor mention id is an Identity AAID, not a routing UUID. */
	id?: string;
	identityAccountId: string;
};

export type AgentMentionInDocument = {
	mentionNodeId: string;
	mentionLocalId?: string;
};

export type EditorMentionChange = {
	id: string;
	localId: string;
	method?: 'pasted' | 'typed';
	type: 'added' | 'deleted';
};

const getDraftAgentMentions = (
	draftMessage: DocNode | null | undefined,
): AgentMentionInDocument[] => {
	const agentMentions: AgentMentionInDocument[] = [];

	const visit = (node: DraftNode | undefined): void => {
		if (!node) {
			return;
		}

		const mentionNodeId = node.attrs?.id?.trim();
		const appType = node.attrs?.appType;
		if (
			node.type === 'mention' &&
			node.attrs?.userType === 'APP' &&
			(appType === undefined || appType === 'agent') &&
			mentionNodeId
		) {
			agentMentions.push({
				mentionNodeId,
				...(node.attrs?.localId ? { mentionLocalId: node.attrs.localId } : {}),
			});
		}

		node.content?.forEach(visit);
	};

	visit(draftMessage as DraftNode | undefined);
	return agentMentions;
};

const getMentionNodeIdCounts = (
	agentMentions: readonly AgentMentionInDocument[],
): Map<string, number> => {
	const counts = new Map<string, number>();
	for (const { mentionNodeId } of agentMentions) {
		counts.set(mentionNodeId, (counts.get(mentionNodeId) ?? 0) + 1);
	}
	return counts;
};

const getAddedMentionNodeIds = (
	previousAgentMentions: readonly AgentMentionInDocument[],
	nextAgentMentions: readonly AgentMentionInDocument[],
): string[] => {
	const remainingPreviousCounts = getMentionNodeIdCounts(previousAgentMentions);
	return nextAgentMentions.flatMap(({ mentionNodeId }) => {
		const remainingCount = remainingPreviousCounts.get(mentionNodeId) ?? 0;
		if (remainingCount === 0) {
			return [mentionNodeId];
		}
		remainingPreviousCounts.set(mentionNodeId, remainingCount - 1);
		return [];
	});
};

const getRemovedMentionNodeIds = (
	previousAgentMentions: readonly AgentMentionInDocument[],
	nextAgentMentions: readonly AgentMentionInDocument[],
): string[] => {
	const remainingNextCounts = getMentionNodeIdCounts(nextAgentMentions);
	const removedMentionNodeIds = previousAgentMentions.flatMap(({ mentionNodeId }) => {
		const remainingCount = remainingNextCounts.get(mentionNodeId) ?? 0;
		if (remainingCount === 0) {
			return [mentionNodeId];
		}
		remainingNextCounts.set(mentionNodeId, remainingCount - 1);
		return [];
	});
	return [...new Set(removedMentionNodeIds)];
};

type UseReconcileSelectedAgentsWithEditorMentionsArgs = {
	enableAgentMentions: boolean;
	promptDraftAndModeKey: string;
	draftMessage: DocNode | null | undefined;
	selectedAgentTransitionHandle: SelectedAgentTransitionHandle;
	selectedAgentIdsRef: MutableRefObject<readonly string[]>;
	deactivatedAgentIdsRef: MutableRefObject<readonly string[]>;
	addSelectedAgentId: (agentId: string, metadata?: RestoredAgentMentionMetadata) => void;
	removeSelectedAgentId: (agentId: string) => void;
};

export type AgentMentionDocumentReconciler = {
	reconcileAgentMentions: (agentMentions: readonly AgentMentionInDocument[]) => void;
	handleEditorMentionsChanged: (changes: readonly EditorMentionChange[]) => void;
};

/**
 * Reconciles editor mention changes with agent-level prompt selection.
 *
 * Document snapshots detect net additions and removals. The editor's paste
 * notification covers identical replacements whose before/after snapshots
 * contain the same agent ids.
 */
export const useReconcileSelectedAgentsWithEditorMentions = ({
	enableAgentMentions,
	promptDraftAndModeKey,
	draftMessage,
	selectedAgentTransitionHandle,
	selectedAgentIdsRef,
	deactivatedAgentIdsRef,
	addSelectedAgentId,
	removeSelectedAgentId,
}: UseReconcileSelectedAgentsWithEditorMentionsArgs): AgentMentionDocumentReconciler => {
	const hasDraftMessage = draftMessage != null;
	const draftAgentMentions = useMemo(() => getDraftAgentMentions(draftMessage), [draftMessage]);
	const agentMentionsByPromptKeyRef = useRef(new Map<string, readonly AgentMentionInDocument[]>());
	const pendingPastedMentionsByPromptKeyRef = useRef(
		new Map<string, readonly EditorMentionChange[]>(),
	);

	const activatePastedAgentMentions = useCallback(
		(agentMentions: readonly AgentMentionInDocument[]) => {
			const pendingPastedMentions =
				pendingPastedMentionsByPromptKeyRef.current.get(promptDraftAndModeKey) ?? [];
			if (pendingPastedMentions.length === 0) {
				return;
			}

			const agentMentionIdsByLocalId = new Map(
				agentMentions.flatMap(({ mentionLocalId, mentionNodeId }) =>
					mentionLocalId ? [[mentionLocalId, mentionNodeId] as const] : [],
				),
			);
			for (const change of pendingPastedMentions) {
				if (agentMentionIdsByLocalId.get(change.localId) === change.id) {
					addSelectedAgentId(change.id, {
						identityAccountId: change.id,
					});
				}
			}
			pendingPastedMentionsByPromptKeyRef.current.delete(promptDraftAndModeKey);
		},
		[promptDraftAndModeKey, addSelectedAgentId],
	);

	const reconcileAgentMentions = useCallback(
		(nextAgentMentions: readonly AgentMentionInDocument[]) => {
			if (!enableAgentMentions) {
				return;
			}

			const previousAgentMentions = agentMentionsByPromptKeyRef.current.get(promptDraftAndModeKey);
			agentMentionsByPromptKeyRef.current.set(promptDraftAndModeKey, [...nextAgentMentions]);

			if (selectedAgentTransitionHandle.isSubmitClearing()) {
				pendingPastedMentionsByPromptKeyRef.current.delete(promptDraftAndModeKey);
				return;
			}

			if (previousAgentMentions === undefined) {
				if (selectedAgentIdsRef.current.length === 0) {
					const deactivatedAgentIdSet = new Set(deactivatedAgentIdsRef.current);
					for (const { mentionNodeId } of nextAgentMentions) {
						if (!deactivatedAgentIdSet.has(mentionNodeId)) {
							addSelectedAgentId(mentionNodeId, {
								identityAccountId: mentionNodeId,
							});
						}
					}
				}
				activatePastedAgentMentions(nextAgentMentions);
				return;
			}

			for (const removedMentionNodeId of getRemovedMentionNodeIds(
				previousAgentMentions,
				nextAgentMentions,
			)) {
				removeSelectedAgentId(removedMentionNodeId);
			}
			for (const addedMentionNodeId of getAddedMentionNodeIds(
				previousAgentMentions,
				nextAgentMentions,
			)) {
				addSelectedAgentId(addedMentionNodeId, {
					identityAccountId: addedMentionNodeId,
				});
			}
			activatePastedAgentMentions(nextAgentMentions);
		},
		[
			activatePastedAgentMentions,
			addSelectedAgentId,
			deactivatedAgentIdsRef,
			enableAgentMentions,
			promptDraftAndModeKey,
			removeSelectedAgentId,
			selectedAgentIdsRef,
			selectedAgentTransitionHandle,
		],
	);

	const handleEditorMentionsChanged = useCallback(
		(changes: readonly EditorMentionChange[]) => {
			if (!enableAgentMentions) {
				return;
			}
			const pastedMentions = changes.filter(
				(change) => change.type === 'added' && change.method === 'pasted',
			);
			if (pastedMentions.length === 0) {
				return;
			}
			pendingPastedMentionsByPromptKeyRef.current.set(promptDraftAndModeKey, pastedMentions);
		},
		[enableAgentMentions, promptDraftAndModeKey],
	);

	useEffect(() => {
		if (hasDraftMessage) {
			reconcileAgentMentions(draftAgentMentions);
		}
	}, [draftAgentMentions, hasDraftMessage, reconcileAgentMentions]);

	return { reconcileAgentMentions, handleEditorMentionsChanged };
};
